import { useState, useCallback, useRef, useEffect } from 'react';

import { useApi } from '@paradigm/api';
import { ONE_CLICK_TRADING, track } from '@paradigm/analytics/src/analytics';
import { useSubmitOrderWithRetries } from '@paradigm/features/src/unified-rfqs/ui/submit-order';
import { useNotificationHandlerActions } from '#/unified-rfqs/ui/notifications/notifications-handler-context';
import {
  Report,
  Status,
  SubmitCallback,
} from '#/unified-rfqs/ui/one-click-trade/types';
import { createFailedResp } from '#/unified-rfqs/ui/one-click-trade/fetch-functions';
import { logException } from '@paradigm/logging/src/logging';
import type { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import type { RfqOrder, Order } from '#/unified-rfqs/entities/drfq/orders';

interface Args {
  order?: RfqOrder;
  rfq: Drfq;
  side: Order['side'];
}

const CONFIRM_WINDOW_MS = 3000;

export default function useSubmitAggressOrder({ order, rfq, side }: Args) {
  const resetStateTimeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const [status, setStatus] = useState<Status>('idle');
  const [lockedOrder, setLockedOrder] = useState<RfqOrder | undefined>();
  const submitOrderWithRetries = useSubmitOrderWithRetries();
  const [, createOrder] = useApi(submitOrderWithRetries);
  const { pauseNotificationsFor, resumeNotifications } =
    useNotificationHandlerActions();

  useEffect(() => {
    return () => clearTimeout(resetStateTimeoutIdRef.current);
  }, []);
  const submitAggressOrder = useCallback(
    async (cb: SubmitCallback<Report>) => {
      if (order === undefined) {
        logException(
          'RFQ Invalid state: Order is null when trying to enter confirm window.',
          null,
        );
        const failedResp = createFailedResp(
          `Failed to cross order. Please try again.`,
        );

        cb(failedResp);
        return;
      }

      if (status !== 'confirming') {
        setStatus('confirming');
        pauseNotificationsFor(CONFIRM_WINDOW_MS);
        // Lock order with top of the book order at the moment of sell/buy click
        setLockedOrder(order);

        clearTimeout(resetStateTimeoutIdRef.current);
        resetStateTimeoutIdRef.current = setTimeout(() => {
          setLockedOrder(undefined);
          setStatus('idle');
        }, CONFIRM_WINDOW_MS);
        return;
      }

      resumeNotifications();
      if (lockedOrder === undefined) {
        logException(
          'RFQ Invalid state: Order is null when trying to confirm cross action.',
          null,
        );
        const failedResp = createFailedResp(
          `Failed to cross order. Please try again.`,
        );

        cb(failedResp);
        return;
      }

      if (rfq.account_name == null) {
        const failedResp = createFailedResp(
          `Could not retrieve ${rfq.venue} default account. Please try again.`,
        );

        cb(failedResp);
        return;
      }

      setStatus('submitting');

      createOrder(
        {
          quantity: rfq.quantity,
          account_name: rfq.account_name,
          side,
          rfq_id: rfq.id,
          type: 'LIMIT',
          time_in_force: 'FILL_OR_KILL',
          price: lockedOrder.price,
        },
        (res) => {
          const event = res.ok
            ? ONE_CLICK_TRADING.QUOTE_SENT_SUCCESS
            : ONE_CLICK_TRADING.QUOTE_SENT_ERROR;

          setLockedOrder(undefined);
          track(event);
          cb(res);
          setStatus('idle');
        },
      );
    },
    [
      order,
      status,
      resumeNotifications,
      lockedOrder,
      rfq,
      createOrder,
      side,
      pauseNotificationsFor,
    ],
  );

  const resetConfirmState = useCallback(() => {
    if (status !== 'confirming') return;

    clearTimeout(resetStateTimeoutIdRef.current);
    resetStateTimeoutIdRef.current = undefined;
    setStatus('idle');
    resumeNotifications();
    setLockedOrder(undefined);
  }, [resumeNotifications, status]);

  return {
    resetConfirmState,
    status,
    lockedOrder,
    submitAggressOrder,
  };
}
